import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import AppIcon from './AppIcon'
import {
  androidBody,
  androidCaption,
  androidTitle,
  ANDROID_FONT_STACK,
  DESCRIPTION_LINE_CLAMP,
  multiLineClamp,
  previewColors,
  singleLineClamp,
} from './previewTheme'
import type { LiveActivityPlatformProps } from './types'

const t = buildTranslate('live_updates.preview')

// Material notification chrome (Figma 430:6267): a 4px-radius surface card,
// Roboto text, and a fully-rounded 3-segment progress bar.
const CARD_RADIUS = '4px'
const CARD_PADDING = '16px'
const ICON_SIZE = '24px'
const ICON_RADIUS = '4px'
const SEGMENT_HEIGHT = '4px'
const SEGMENT_RADIUS = '999px'
const SEGMENT_GAP = '8px'
// Caption separator between merchant name and ETA in the header.
const CAPTION_SEPARATOR = ' · '

export interface AndroidLiveActivityCardProps
  extends LiveActivityPlatformProps {
  // Merchant display name, shown in the header caption
  // ("{merchantName} · {eta}"). Optional: when absent the caption is the ETA
  // alone.
  merchantName?: string
  // A canceled / out-of-track stage shows no progress bar and drops the ETA
  // from the header, matching the design's "Cancelled" card.
  isCanceled: boolean
}

// The Android Live Update card: a Material notification (green-tinted surface,
// Roboto, on-surface text) with a merchant caption and a fully-rounded
// segmented progress bar — no dots, no stage names — matching the Figma Android
// Live Update design (430:6267).
const AndroidLiveActivityCard = ({
  content,
  appIconUrl,
  track,
  activeIndex,
  confirmedOnly,
  merchantName,
  isCanceled,
}: AndroidLiveActivityCardProps) => {
  const [css] = useStyletron()

  // Header caption: "{merchantName} · {eta}", the ETA alone when no merchant
  // name is given, and the merchant name alone (no ETA) once the order is
  // canceled / past the track.
  const captionParts = [merchantName, isCanceled ? undefined : t('eta')].filter(
    (part): part is string => Boolean(part)
  )
  const caption = captionParts.join(CAPTION_SEPARATOR)

  const renderSegmentedBar = () => (
    <div
      aria-hidden="true"
      className={css({ display: 'flex', gap: SEGMENT_GAP })}
    >
      {track.map((stage, index) => {
        const isFilled = index <= activeIndex
        return (
          <div
            key={stage}
            data-testid="lu-preview-segment"
            data-filled={isFilled}
            className={css({
              flex: 1,
              height: SEGMENT_HEIGHT,
              borderRadius: SEGMENT_RADIUS,
              // Completed = brand red; remaining = white over the Material
              // surface (Figma 596:13460).
              backgroundColor: isFilled
                ? previewColors.brandRed
                : palette.white,
            })}
          />
        )
      })}
    </div>
  )

  return (
    <div
      className={css({
        backgroundColor: previewColors.androidSurface,
        borderRadius: CARD_RADIUS,
        padding: CARD_PADDING,
        fontFamily: ANDROID_FONT_STACK,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      })}
    >
      {/* Header: app icon + Material caption together on the left. */}
      <div
        className={css({ display: 'flex', alignItems: 'center', gap: '14px' })}
      >
        <AppIcon
          appIconUrl={appIconUrl}
          size={ICON_SIZE}
          radius={ICON_RADIUS}
        />
        {caption && <span className={css(androidCaption)}>{caption}</span>}
      </div>

      {/* Content: title (on-surface) + body (on-surface-variant). */}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        })}
      >
        <span
          title={content.title}
          className={css({ ...androidTitle, ...singleLineClamp })}
        >
          {content.title}
        </span>
        <span
          title={content.liveText}
          className={css({
            ...androidBody,
            ...multiLineClamp(DESCRIPTION_LINE_CLAMP),
          })}
        >
          {content.liveText}
        </span>
      </div>

      {!confirmedOnly && !isCanceled && renderSegmentedBar()}
    </div>
  )
}

export default AndroidLiveActivityCard
